/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import React, { useState } from 'react';
import {
    SELECTION_MODE,
    STACK_DIRECTION,
    STACK_GAP,
    StackView,
    ErrorMessage,
    Icon,
    LoginBackIcon,
    INDENT,
    STACK_ALIGN,
    Text,
    Spinner,
    TEXT_VIEW,
    GrowView,
    SIZE_S,
} from '@veeam/components';

import type { CellRendererProps } from '@veeam/components/lib/Grid/types';
import type { ListClickEventProps, IconType } from '@veeam/components';

import { DataList } from '../../../DataList';
import { StyledLinkButton } from '../LoginButtons';
import { LoginTitle } from '../LoginTitle';
import { LOGIN_FORM_WIDTH } from '../FormContainer';

import type { SelectServerFormState } from '../../interfaces';

export interface SelectServerFormProps<T extends Record<string, string>> {
    data: T[];
    valueGetter: (option: T) => any;
    textGetter: (option: T) => string;
    logIn: (value: SelectServerFormState) => void;
    title?: string;
    returnButtonText?: string;
    onReturnButtonClick?: (e: React.MouseEvent) => void;
    iconGetter?: (disabled: boolean) => IconType | null;
    loading?: boolean;
    error?: string;
    formWidth?: number;
    itemHeight?: number;
    maxItemsRow?: number;
}

export const SelectServerForm = <T extends Record<string, string>, >(props: SelectServerFormProps<T>): React.ReactElement => {
    const {
        title = 'Please choose a site to log in',
        returnButtonText = 'Back',
        onReturnButtonClick,
        data,
        valueGetter,
        textGetter,
        iconGetter,
        loading,
        error,
        logIn,
        formWidth = LOGIN_FORM_WIDTH,
        itemHeight = 48,
        maxItemsRow = 10,
    } = props;
    const itemsHeight = (data.length || 1) * itemHeight;
    const listHeight = Math.min(itemHeight * maxItemsRow, itemsHeight);
    const [selected, setSelected] = useState<string>(null);
    const itemRenderer = ({ cellData, isSelected, disabled }: CellRendererProps<T> & { isSelected: boolean; }) => (
        <StackView align={STACK_ALIGN.fill} size={{ height: `${itemHeight}px`, width: `${formWidth}px` }}>
            <StackView gap={STACK_GAP.s} align={STACK_ALIGN.start} spaceTop={INDENT.m} spaceLeft={INDENT.m}>
                {iconGetter && iconGetter(disabled)}

                <Text
                    view={loading ? TEXT_VIEW.secondary : TEXT_VIEW.primary}
                    truncate
                    style={{ width: `${isSelected ? (formWidth - (parseInt(SIZE_S, 10) * 11)) : (formWidth - (parseInt(SIZE_S, 10) * 5))}px` }}
                >
                    {cellData}
                </Text>
            </StackView>

            <GrowView />

            {isSelected && (
                <StackView spaceTop={INDENT.s} spaceRight={INDENT.s}>
                    <Spinner />
                </StackView>
            )}
        </StackView>
    );

    return (
        <StackView direction={STACK_DIRECTION.column} gap={STACK_GAP.s} size={{ width: `${formWidth}px` }}>
            {onReturnButtonClick && (
                <StyledLinkButton
                    compact
                    disabled={loading}
                    onClick={onReturnButtonClick}
                    iconBefore={<Icon src={<LoginBackIcon />} />}
                >
                    {returnButtonText}
                </StyledLinkButton>
            )}

            <LoginTitle text={title} />

            <StackView direction={STACK_DIRECTION.column} gap={STACK_GAP.s} spaceTop={error ? INDENT.none : INDENT.m}>
                {error && <ErrorMessage>{error}</ErrorMessage>}

                <StackView direction={STACK_DIRECTION.column} gap={STACK_GAP.m} size={{ height: `${listHeight}px` }}>
                    <DataList
                        disabled={loading}
                        selectionMode={SELECTION_MODE.single}
                        data={data}
                        valueGetter={valueGetter}
                        textGetter={textGetter}
                        cellRenderer={(props: CellRendererProps<T>) => itemRenderer({ ...props, isSelected: props.rowData.value === selected })}
                        itemHeight={itemHeight}
                        onChange={(selected: string[]) => setSelected(selected[0])}
                        onClick={({ rowIndex }: ListClickEventProps<T>) => logIn({ index: `${rowIndex}` })}
                    />
                </StackView>
            </StackView>
        </StackView>
    );
};
